/**
 * Phase 61 Plan 06 — Side-by-side band distribution preview chart (D-22).
 *
 * Shows two bar charts: current vs proposed band distributions.
 * Deliberately read-only — displays preview only, no save action.
 */
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import type { BandCounts, PreviewResultDTO } from '../../../services/api/respectAdmin';

// ── Helpers ───────────────────────────────────────────────────────────────────

const BAND_LABELS: Record<keyof BandCounts, string> = {
  small: 'Small',
  medium: 'Medium',
  'spotlight-level': 'Spotlight',
};

const BAND_COLORS: Record<keyof BandCounts, string> = {
  small: '#9CA3AF',      // gray
  medium: '#60A5FA',     // blue
  'spotlight-level': '#F59E0B', // amber
};

function toChartData(counts: BandCounts) {
  return (Object.keys(BAND_LABELS) as (keyof BandCounts)[]).map((key) => ({
    band: BAND_LABELS[key],
    count: counts[key] ?? 0,
    fill: BAND_COLORS[key],
  }));
}

// ── SubChart: one bar chart for current or proposed ───────────────────────────

function BandDistChart({ title, counts }: { title: string; counts: BandCounts }) {
  const data = toChartData(counts);
  return (
    <div className="flex flex-col items-center">
      <p className="text-sm font-medium text-gray-700 mb-2">{title}</p>
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={data} margin={{ top: 4, right: 16, bottom: 4, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="band" tick={{ fontSize: 11 }} />
          <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
          <Tooltip />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Bar dataKey="count" name="Users" isAnimationActive={false}>
            {data.map((entry) => (
              <Cell key={entry.band} fill={entry.fill} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

interface RecomputePreviewChartProps {
  preview: PreviewResultDTO;
}

export function RecomputePreviewChart({ preview }: RecomputePreviewChartProps) {
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-6">
        <BandDistChart title="Current distribution" counts={preview.current} />
        <BandDistChart title="Proposed distribution" counts={preview.proposed} />
      </div>
      <p className="text-xs text-center text-gray-400 italic">
        Preview only — not saved. Bands computed against last 30-day window if these weights had applied.
      </p>
    </div>
  );
}
